/**
 * Unified-trash restore composable (single + bulk).
 *
 * Classifies the selected deleted items, shows the "restore to a new collection?"
 * confirm dialog ONLY for items whose source collection still exists but the user
 * can't add to (deleted-collection items restore into a new collection silently),
 * then calls the global restore endpoint. Shared by the single and bulk paths (DRY).
 */
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import toast from '@/plugins/toasts'
import useConfirmModal from './useConfirmModal'

export function useRestoreDeletedItems() {
  const store = useStore()
  const { t } = useI18n()
  const { openModal } = useConfirmModal()

  // Alive collection the user lacks add-permission on -> needs explicit consent to
  // restore into a NEW collection instead.
  const needsNewCollectionConfirm = item =>
    !!item.sourceCollection && !item.sourceCollection.deleted && !item.sourceCollection.canRestoreHere

  /**
   * @param {Array} items CollectionItem records to restore
   * @returns {Object|null} { restored, skipped, createdCollectionIds } or null if cancelled
   */
  async function restoreItems(items) {
    if (!items.length) return null

    const confirmItems = items.filter(needsNewCollectionConfirm)
    let forceNewCollectionItemIds = []

    if (confirmItems.length) {
      const collectionNames = [...new Set(confirmItems.map(i => i.sourceCollection?.name).filter(Boolean))].join(', ')
      const dialog = await openModal('confirm', {
        payload: {
          title: t('page.bookmarks.deleted.restore_new_collection.title'),
          text: t('page.bookmarks.deleted.restore_new_collection.text', {
            count: confirmItems.length,
            collections: collectionNames,
          }),
          confirmBtnText: t('page.bookmarks.deleted.restore_new_collection.confirm'),
        },
      })
      const confirmed = await dialog.result
      if (!confirmed) return null // Cancel aborts the whole operation.
      forceNewCollectionItemIds = confirmItems.map(i => i.id)
    }

    const result = await store.dispatch('collectionItems/restoreDeleted', {
      itemIds: items.map(i => i.id),
      forceNewCollectionItemIds,
    })

    const restoredCount = result?.restored?.length ?? 0
    const skippedCount = result?.skipped?.length ?? 0

    if (restoredCount) toast.info(t('page.bookmarks.deleted.toast.restored'))
    if (skippedCount) toast.info(t('page.bookmarks.deleted.toast.restore_skipped'))

    return result
  }

  return { restoreItems, needsNewCollectionConfirm }
}
